import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Key,
  Plus,
  RefreshCw,
  Trash2,
  Copy,
  CheckCircle,
  AlertCircle,
  Clock,
  Shield,
  X,
} from 'lucide-react';
import { config } from '../config';

interface ApiKey {
  keyPrefix: string;
  userId: string;
  name: string;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'deprecated' | 'expired' | 'revoked';
  rateLimit: number;
}

// The admin API attributes every key to a person or a service account.
const ActorType = {
  HUMAN: 'human',
  SERVICE: 'service',
} as const;
type ActorType = (typeof ActorType)[keyof typeof ActorType];

interface CreateKeyResponse {
  apiKey: string;
  keyPrefix: string;
  userId: string;
  name: string;
  expiresAt: string;
}

interface RotateKeyResponse {
  newApiKey: string;
  newKeyPrefix: string;
  oldKeyPrefix: string;
  deprecationExpiry: string;
  expiresAt: string;
}

async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  // Use ID token because it contains the email claim needed for admin verification
  return session.tokens?.idToken?.toString() || '';
}

async function fetchApiKeys(): Promise<ApiKey[]> {
  const token = await getAuthToken();
  const response = await fetch(`${config.mcp.adminEndpoint}/keys`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch API keys');
  }

  const data = await response.json();
  return data.keys;
}

async function createApiKey(params: {
  userId: string;
  name: string;
  displayName: string;
  actorType: ActorType;
  days: number;
}): Promise<CreateKeyResponse> {
  const token = await getAuthToken();
  const response = await fetch(`${config.mcp.adminEndpoint}/keys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create API key');
  }

  return response.json();
}

async function rotateApiKey(keyPrefix: string): Promise<RotateKeyResponse> {
  const token = await getAuthToken();
  const response = await fetch(`${config.mcp.adminEndpoint}/keys/${keyPrefix}/rotate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to rotate API key');
  }

  return response.json();
}

async function revokeApiKey(keyPrefix: string): Promise<void> {
  const token = await getAuthToken();
  const response = await fetch(`${config.mcp.adminEndpoint}/keys/${keyPrefix}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to revoke API key');
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
          <Shield className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    case 'deprecated':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
          <Clock className="w-3 h-3 mr-1" />
          Deprecated
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">
          <Clock className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    case 'revoked':
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
          <AlertCircle className="w-3 h-3 mr-1" />
          Revoked
        </span>
      );
    default:
      return null;
  }
}

export default function ApiKeyManagement() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<CreateKeyResponse | RotateKeyResponse | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const emptyCreateForm = {
    userId: '',
    name: '',
    displayName: '',
    actorType: ActorType.HUMAN as ActorType,
    days: 90,
  };
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const {
    data: apiKeys,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: fetchApiKeys,
  });

  const createMutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      setShowCreateModal(false);
      setNewKeyData(data);
      setShowNewKeyModal(true);
      setCreateForm(emptyCreateForm);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: rotateApiKey,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      setNewKeyData(data);
      setShowNewKeyModal(true);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      setConfirmRevoke(null);
    },
  });

  const copyApiKey = async (key: string) => {
    await navigator.clipboard.writeText(key);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(createForm);
  };

  const getNewKey = () => {
    if (!newKeyData) return '';
    return 'apiKey' in newKeyData ? newKeyData.apiKey : newKeyData.newApiKey;
  };

  if (error) {
    return (
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load API keys. Make sure you have admin access.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
            <Key className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">API Key Management</h2>
            <p className="text-sm text-gray-text">Manage long-lived API keys for MCP access</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg bg-gray-800 text-gray-text hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Key</span>
          </button>
        </div>
      </div>

      {/* Keys Table */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary mb-2" />
            <p className="text-gray-text">Loading API keys...</p>
          </div>
        ) : apiKeys && apiKeys.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-border">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-text">Key ID</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-text">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-text">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-text">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-text">Expires</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-text">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.keyPrefix} className="border-b border-gray-border/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3">
                      <code className="text-sm text-primary">{key.keyPrefix}...</code>
                    </td>
                    <td className="px-4 py-3 text-white">{key.name}</td>
                    <td className="px-4 py-3 text-gray-text">{key.userId}</td>
                    <td className="px-4 py-3">{getStatusBadge(key.status)}</td>
                    <td className="px-4 py-3 text-gray-text text-sm">
                      {new Date(key.expiresAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2">
                        {key.status === 'active' && (
                          <>
                            <button
                              onClick={() => rotateMutation.mutate(key.keyPrefix)}
                              disabled={rotateMutation.isPending}
                              className="p-2 rounded-lg bg-gray-800 text-gray-text hover:text-white transition-colors disabled:opacity-50"
                              title="Rotate key"
                            >
                              <RefreshCw className={`w-4 h-4 ${rotateMutation.isPending ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              onClick={() => setConfirmRevoke(key.keyPrefix)}
                              className="p-2 rounded-lg bg-gray-800 text-gray-text hover:text-red-400 transition-colors"
                              title="Revoke key"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Key className="w-8 h-8 mx-auto text-gray-text mb-2" />
            <p className="text-gray-text">No API keys found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create your first key</span>
            </button>
          </div>
        )}
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-card w-full max-w-md p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Create API Key</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-text hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-text mb-1">User ID</label>
                <input
                  type="text"
                  value={createForm.userId}
                  onChange={(e) => setCreateForm({ ...createForm, userId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., admin, developer"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-text mb-1">Key Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., Claude Code, Production"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="api-key-display-name"
                  className="block text-sm font-medium text-gray-text mb-1"
                >
                  Display Name
                </label>
                <input
                  id="api-key-display-name"
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="e.g., Jane Doe (Claude Code)"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="api-key-actor-type"
                  className="block text-sm font-medium text-gray-text mb-1"
                >
                  Used By
                </label>
                <select
                  id="api-key-actor-type"
                  value={createForm.actorType}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, actorType: e.target.value as ActorType })
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  <option value={ActorType.HUMAN}>A person</option>
                  <option value={ActorType.SERVICE}>A service</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-text mb-1">
                  Validity (days)
                </label>
                <select
                  value={createForm.days}
                  onChange={(e) => setCreateForm({ ...createForm, days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>180 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              {createMutation.error && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {createMutation.error.message}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-gray-text hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary flex items-center space-x-2"
                >
                  {createMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span>Create Key</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Key Modal */}
      {showNewKeyModal && newKeyData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-card w-full max-w-lg p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>{'apiKey' in newKeyData ? 'API Key Created' : 'API Key Rotated'}</span>
              </h3>
              <button
                onClick={() => {
                  setShowNewKeyModal(false);
                  setNewKeyData(null);
                }}
                className="text-gray-text hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 font-medium">Save this key now!</p>
                    <p className="text-yellow-400/80 text-sm">
                      This is the only time you'll see the full API key. Copy it and store it securely.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-text mb-2">API Key</label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-900 rounded-lg p-3 font-mono text-sm text-green-400 overflow-x-auto">
                    {getNewKey()}
                  </div>
                  <button
                    onClick={() => copyApiKey(getNewKey())}
                    className={`p-3 rounded-lg transition-colors ${
                      keyCopied
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-800 text-gray-text hover:text-white'
                    }`}
                  >
                    {keyCopied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {'newKeyPrefix' in newKeyData && (
                <div className="text-sm text-gray-text">
                  <p>
                    Old key <code className="text-yellow-400">{newKeyData.oldKeyPrefix}...</code> will
                    continue working until{' '}
                    {new Date(newKeyData.deprecationExpiry).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="pt-4">
                <button
                  onClick={() => {
                    setShowNewKeyModal(false);
                    setNewKeyData(null);
                  }}
                  className="w-full btn-primary"
                >
                  I've saved the key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Revoke Modal */}
      {confirmRevoke && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-card w-full max-w-md p-6 m-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Revoke API Key</h3>
                <p className="text-sm text-gray-text">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-text mb-6">
              Are you sure you want to revoke the key{' '}
              <code className="text-red-400">{confirmRevoke}...</code>? It will immediately stop
              working.
            </p>

            {revokeMutation.error && (
              <div className="p-3 mb-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {revokeMutation.error.message}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmRevoke(null)}
                className="px-4 py-2 rounded-lg bg-gray-800 text-gray-text hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => revokeMutation.mutate(confirmRevoke)}
                disabled={revokeMutation.isPending}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {revokeMutation.isPending && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>Revoke Key</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
